import { useMemo } from 'react';
import { Flex, Tooltip } from 'antd';
import { Button } from '../../../button';
import { ZoomInOutlined, ZoomOutOutlined, ExpandOutlined, CompressOutlined, WarningOutlined } from '@ant-design/icons';
import RqiPhizChart from './components/Charts/RqiPhizChart/RqiPhizChart';
import PermPorosityChart from './components/Charts/PermPorosityChart/PermPorosityChart';
import StratigraphicLorenzChart from './components/Charts/StratigraphicLorenzChart/StratigraphicLorenzChart';
import DistributionChart from './components/Charts/distributionChart';
import MeanChart from './components/Charts/meanChart';
import MedianChart from './components/Charts/medianChart';
import { TCorrelationValue } from '../../types';
import { Accordion } from '../../../accordion';
import { transformChartData } from '../../utils/transformChartData';
import { useAppContext } from '../../../../providers/AppContext';
import { useCorrelation } from '../../../../providers/Correlation';
import { ProjectSample } from '../../../../@types/samples/types';
import { useDimensionControl } from '../../hook/useDimensionControl';
import { isFlowUnitsEnabled } from '../../../../utils/env';
import { LazyMount } from '../../../../components/chart/LazyMount';

const HEIGHT_OPTIONS = ['60%', '70%', '80%', '90%', '100%'];
const WIDTH_OPTIONS = ['19%', '24%', '32%', '49%', '100%'];

const ANALYSIS_SYMBOLS: Record<string, string> = {
  liao: 'circle',
  'water-oil': 'square',
  'gas-oil': 'triangle',
};

const getMarkerSymbol = (types: string[]): string =>
  types.length > 1 ? 'diamond' : (ANALYSIS_SYMBOLS[types[0]] ?? 'circle');

const mergeAndDeduplicateSamples = (
  liao: ProjectSample[],
  waterOil: ProjectSample[],
  gasOil: ProjectSample[]
): Array<{ sample: ProjectSample; analysisTypes: string[] }> => {
  const map = new Map<string, { sample: ProjectSample; types: Set<string> }>();

  const add = (samples: ProjectSample[], type: string) =>
    samples.forEach(s => {
      if (!map.has(s.sample_code)) map.set(s.sample_code, { sample: s, types: new Set() });
      map.get(s.sample_code)!.types.add(type);
    });

  add(liao, 'liao');
  add(waterOil, 'water-oil');
  add(gasOil, 'gas-oil');

  return Array.from(map.values()).map(({ sample, types }) => ({
    sample,
    analysisTypes: Array.from(types),
  }));
};

const transformPermeabilityField = (
  entries: Array<{ sample: ProjectSample; analysisTypes: string[] }>,
  wellColorMap: Record<string, string | null>,
  selectedXAxis: 'fzi' | 'rqi'
) => {
  return entries.map(({ sample, analysisTypes }) => {
    const color = wellColorMap[sample.well_uid] || undefined;
    const fzi = sample.input_sample_values.fzi || 0;
    const rqi = sample.input_sample_values.rqi || 0;
    const porosity = sample.input_sample_values.porosity ?? 0;
    const phiz = sample.input_sample_values.phiz ?? (porosity > 0 && porosity < 1 ? porosity / (1 - porosity) : 0);
    const code = sample.sample_code;
    const rawValue = sample.input_sample_values.permeability ?? 0;
    const symbol = getMarkerSymbol(analysisTypes);
    return transformChartData(rawValue, selectedXAxis === 'fzi' ? fzi : rqi, code, true, color, porosity, undefined, phiz, rqi, symbol, analysisTypes);
  });
};

export function Zones() {
  const { data } = useAppContext();
  const { localProject, checkedLiaoSamples, checkedWaterOilSamples, checkedGasOilSamples, selectedXAxis } = useCorrelation();
  const xAxisLabel = selectedXAxis.toUpperCase();

  const zones = useMemo(() => localProject?.correlation?.zones, [localProject]);
  const hfuBands = useMemo(() => localProject?.correlation?.hfu_bands, [localProject]);
  const showFlowUnits = isFlowUnitsEnabled();

  const wellColorMap = useMemo(
    () =>
      data.wells.reduce(
        (map, well) => {
          map[well.uid] = well.color;
          return map;
        },
        {} as Record<string, string | null>
      ),
    [data.wells]
  );

  const mergedSamples = useMemo(
    () => mergeAndDeduplicateSamples(checkedLiaoSamples, checkedWaterOilSamples, checkedGasOilSamples),
    [checkedLiaoSamples, checkedWaterOilSamples, checkedGasOilSamples]
  );

  const permData: TCorrelationValue[] = useMemo(
    () => transformPermeabilityField(mergedSamples, wellColorMap, selectedXAxis),
    [mergedSamples, wellColorMap, selectedXAxis]
  );

  const allCheckedSamples = useMemo(
    () => mergedSamples.map(e => e.sample),
    [mergedSamples]
  );

  const {
    currentValue: chartHeight,
    increase: expand,
    decrease: compress
  } = useDimensionControl(HEIGHT_OPTIONS, 3);

  const {
    currentValue: containerWidth,
    increase: zoomIn,
    decrease: zoomOut
  } = useDimensionControl(WIDTH_OPTIONS, 3);

  return (
    <Accordion defaultOpen="zones">
      <Accordion.Item id="zones" header="Rock Type Charts and Statistics">
      <div style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {zones?.length === 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'red' }}>
                <WarningOutlined />
                <p style={{ margin: 0 }}>User must create at least one zone</p>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Tooltip title="Zoom in charts">
              <Button variant="ghost" size="sm" startIcon={<ZoomInOutlined />} onClick={zoomIn} />
            </Tooltip>
            <Tooltip title="Zoom out charts">
              <Button variant="ghost" size="sm" startIcon={<ZoomOutOutlined />} onClick={zoomOut} />
            </Tooltip>
            <Tooltip title="Expand charts">
              <Button variant="ghost" size="sm" startIcon={<ExpandOutlined />} onClick={expand} />
            </Tooltip>
            <Tooltip title="Compress charts">
              <Button variant="ghost" size="sm" startIcon={<CompressOutlined />} onClick={compress} />
            </Tooltip>
          </div>
        </div>

        <Flex gap="small" wrap="wrap">
          <LazyMount style={{ width: containerWidth, minHeight: 420 }}>
            <RqiPhizChart permData={permData || []} containerWidth="100%" height={chartHeight} />
          </LazyMount>
          <LazyMount style={{ width: containerWidth, minHeight: 420 }}>
            <PermPorosityChart permData={permData || []} containerWidth="100%" height={chartHeight} />
          </LazyMount>
          {showFlowUnits && (
            <LazyMount style={{ width: containerWidth, minHeight: 420 }}>
              <StratigraphicLorenzChart samples={allCheckedSamples || []} hfuBands={hfuBands} containerWidth="100%" height={chartHeight} />
            </LazyMount>
          )}
          <LazyMount style={{ width: containerWidth, minHeight: 420 }}>
            <DistributionChart permData={permData || []} containerWidth="100%" height={chartHeight} xAxisLabel={xAxisLabel} />
          </LazyMount>
          <LazyMount style={{ width: containerWidth, minHeight: 420 }}>
            <MeanChart permData={permData || []} containerWidth="100%" height={chartHeight} xAxisLabel={xAxisLabel} />
          </LazyMount>
          <LazyMount style={{ width: containerWidth, minHeight: 420 }}>
            <MedianChart permData={permData || []} containerWidth="100%" height={chartHeight} xAxisLabel={xAxisLabel} />
          </LazyMount>
        </Flex>
      </div>
      </Accordion.Item>
    </Accordion>
  );
}
